/**
 * ResumePage — RESPONDENT_WIZARD_SPEC v1.3 §4.4 chemin B.
 *
 * Le respondent saisit le code de reprise copié depuis un autre appareil.
 * Le serveur hashe + timing-safe compare + rotate immédiatement (R-17) +
 * pose le nouveau cookie sur ce device → redirection vers /wizard.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { resumeWizardByCode } from '@/lib/api';
import { storeResumeToken } from '@/lib/resume-token';

export default function ResumePage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  if (!slug) return <div className="p-8 text-center">Lien invalide.</div>;

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!slug) return;
    setBusy(true);
    setErrMsg('');
    try {
      const res = await resumeWizardByCode(slug, code.trim());
      // Backup localStorage (fallback si cookie échoue côté navigateur).
      storeResumeToken(slug, res.resumeToken);
      navigate(`/s/${encodeURIComponent(slug)}/wizard`);
    } catch (err) {
      // 404 anti-fuite → message unique pour invalide / expiré
      setErrMsg(
        'Code introuvable, expiré, ou trop de tentatives. Vérifiez la saisie, ou réessayez dans quelques minutes.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen bg-bg flex items-center justify-center p-4">
      <form onSubmit={submit} className="card max-w-md w-full space-y-4">
        <h1 className="text-xl font-semibold">Reprendre une enquête</h1>
        <p className="text-sm text-text-muted">
          Saisissez le code de reprise que vous avez copié depuis votre autre appareil.
        </p>
        <label className="block">
          <span className="field-label">Code de reprise</span>
          <input
            type="text"
            className="input-base font-mono"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="off"
            autoFocus
            required
            minLength={32}
            disabled={busy}
          />
        </label>
        {errMsg && <p className="field-error">{errMsg}</p>}
        <button type="submit" className="btn-primary w-full" disabled={busy || code.length < 32}>
          {busy ? 'Reprise…' : 'Reprendre'}
        </button>
        <p className="text-xs text-text-muted text-center">
          Si vous avez perdu votre code, vos réponses partielles sont définitivement
          inaccessibles (anonymat absolu). Vous pouvez recommencer.
        </p>
      </form>
    </main>
  );
}
